import { v4 as uuidv4 } from "uuid";

import { SHORT_NAME_METAFIELD_KEY } from "@/domains/careplan/models/constants";
import {
  findCatalogProductByVariantId,
  type MockCatalogProduct,
  toDollars,
} from "@/mocks/data/catalog";
import {
  findDiscountCode,
  findSellingPlanById,
  MOCK_SELLING_PLANS,
  MOCK_SHIPPING_OPTIONS,
  type MockDiscountCode,
  type MockSellingPlan,
} from "@/mocks/data/shopify";
import { type MockCart, type MockCartLine, mockCarts } from "@/mocks/store";
import type {
  CartInput,
  CartLineInput,
  CartLineUpdateInput,
  CurrencyCode,
  DeliveryMethodType,
  SellingPlanInterval,
} from "@/types/storefront-api.gen";

const VARIANT_GID_PREFIX = "gid://shopify/ProductVariant/";
const CURRENCY_CODE: CurrencyCode = "USD";
const DELIVERY_METHOD: DeliveryMethodType = "SHIPPING";
const SELLING_PLAN_INTERVAL: SellingPlanInterval = "MONTH";
const DEFAULT_VARIANT_TITLE = "Default Title";

type CartUserError = {
  field: string[] | null;
  message: string;
};

type ShippingOption = (typeof MOCK_SHIPPING_OPTIONS)[keyof typeof MOCK_SHIPPING_OPTIONS];

type PricedCartLine = {
  line: MockCartLine;
  product: MockCatalogProduct;
  sellingPlan: MockSellingPlan | undefined;
  unitPrice: number;
  perDeliveryPrice: number;
  subtotal: number;
  total: number;
};

const roundToCents = (amount: number) => {
  return Math.round(amount * 100) / 100;
};

const sum = (amounts: number[]) => {
  return roundToCents(
    amounts.reduce((total, amount) => {
      return total + amount;
    }, 0),
  );
};

const money = (amount: number) => {
  return { amount: amount.toFixed(2), currencyCode: CURRENCY_CODE };
};

const toVariantGid = (variantId: string) => {
  return `${VARIANT_GID_PREFIX}${variantId}`;
};

const fromVariantGid = (merchandiseId: string) => {
  return merchandiseId.startsWith(VARIANT_GID_PREFIX)
    ? merchandiseId.slice(VARIANT_GID_PREFIX.length)
    : null;
};

export const toArray = <Item>(value: Item | Item[]) => {
  return Array.isArray(value) ? value : [value];
};

const getDiscountAmount = (discount: MockDiscountCode, baseAmount: number, quantity = 1) => {
  const amount =
    discount.type === "percentage"
      ? (baseAmount * discount.value) / 100
      : discount.value * quantity;

  return roundToCents(Math.min(amount, baseAmount));
};

const addCartLine = (cart: MockCart, input: CartLineInput, field: string[]): CartUserError[] => {
  const variantId = fromVariantGid(input.merchandiseId);
  const product = variantId ? findCatalogProductByVariantId(variantId) : undefined;

  if (!product) {
    return [
      {
        field: [...field, "merchandiseId"],
        message: `The merchandise with id ${input.merchandiseId} does not exist.`,
      },
    ];
  }

  const sellingPlanId = input.sellingPlanId ?? null;

  if (sellingPlanId && !findSellingPlanById(sellingPlanId)) {
    return [
      {
        field: [...field, "sellingPlanId"],
        message: `The selling plan with id ${sellingPlanId} does not exist.`,
      },
    ];
  }

  const quantity = input.quantity ?? 1;
  const existingLine = cart.lines.find((line) => {
    return line.variantId === product.variantId && line.sellingPlanId === sellingPlanId;
  });

  if (existingLine) {
    existingLine.quantity += quantity;
  } else {
    cart.lines.push({
      id: `gid://shopify/CartLine/${uuidv4()}`,
      variantId: product.variantId,
      quantity,
      sellingPlanId,
    });
  }

  return [];
};

const normalizeDiscountCodes = (codes: string[]) => {
  const seenCodes = new Set<string>();

  return codes
    .map((code) => {
      return code.trim();
    })
    .filter((code) => {
      const normalizedCode = code.toUpperCase();

      if (!code || seenCodes.has(normalizedCode)) {
        return false;
      }

      seenCodes.add(normalizedCode);

      return true;
    });
};

export const createMockCart = (input: CartInput) => {
  const token = uuidv4().replaceAll("-", "");
  const cart: MockCart = {
    id: `gid://shopify/Cart/${token}`,
    token,
    lines: [],
    discountCodes: normalizeDiscountCodes(input.discountCodes ?? []),
    attributes: (input.attributes ?? []).map(({ key, value }) => {
      return { key, value };
    }),
  };

  const userErrors = (input.lines ?? []).flatMap((line, index) => {
    return addCartLine(cart, line, ["input", "lines", String(index)]);
  });

  mockCarts.set(cart.id, cart);

  return { cart, userErrors };
};

export const addMockCartLines = (cart: MockCart, lines: CartLineInput[]) => {
  return lines.flatMap((line, index) => {
    return addCartLine(cart, line, ["lines", String(index)]);
  });
};

export const updateMockCartLines = (cart: MockCart, updates: CartLineUpdateInput[]) => {
  return updates.flatMap((update, index): CartUserError[] => {
    const line = cart.lines.find((cartLine) => {
      return cartLine.id === update.id;
    });

    if (!line) {
      return [
        {
          field: ["lines", String(index), "id"],
          message: `The merchandise line with id ${update.id} does not exist.`,
        },
      ];
    }

    if (update.quantity === 0) {
      cart.lines = cart.lines.filter((cartLine) => {
        return cartLine.id !== update.id;
      });

      return [];
    }

    if (typeof update.quantity === "number") {
      line.quantity = update.quantity;
    }

    if (update.sellingPlanId !== undefined) {
      line.sellingPlanId = update.sellingPlanId;
    }

    return [];
  });
};

export const setMockCartDiscountCodes = (cart: MockCart, discountCodes: string[]) => {
  cart.discountCodes = normalizeDiscountCodes(discountCodes);
};

const priceCartLine = (line: MockCartLine, lineDiscounts: MockDiscountCode[]) => {
  const product = findCatalogProductByVariantId(line.variantId);

  if (!product) {
    return [];
  }

  const sellingPlan = line.sellingPlanId ? findSellingPlanById(line.sellingPlanId) : undefined;
  const unitPrice = toDollars(product.priceInCents);
  const perDeliveryPrice = sellingPlan
    ? roundToCents(unitPrice * (1 - sellingPlan.discountPercentage / 100))
    : unitPrice;
  const subtotal = roundToCents(perDeliveryPrice * line.quantity);
  /* A fixed line discount applies per unit, as Shopify's "amount off each product" does. */
  const lineDiscount = sum(
    lineDiscounts.map((discount) => {
      return getDiscountAmount(discount, subtotal, line.quantity);
    }),
  );

  return [
    {
      line,
      product,
      sellingPlan,
      unitPrice,
      perDeliveryPrice,
      subtotal,
      total: roundToCents(Math.max(subtotal - lineDiscount, 0)),
    },
  ];
};

const serializeSellingPlanAllocation = ({
  perDeliveryPrice,
  sellingPlan,
  unitPrice,
}: PricedCartLine) => {
  if (!sellingPlan) {
    return null;
  }

  return {
    sellingPlan: {
      id: sellingPlan.id,
      name: sellingPlan.name,
      description: sellingPlan.description,
      recurringDeliveries: true,
      priceAdjustments: [
        {
          adjustmentValue: {
            __typename: "SellingPlanPercentagePriceAdjustment" as const,
            adjustmentPercentage: sellingPlan.discountPercentage,
          },
        },
      ],
      deliveryPolicy: {
        __typename: "SellingPlanRecurringDeliveryPolicy" as const,
        interval: SELLING_PLAN_INTERVAL,
        intervalCount: sellingPlan.intervalCount,
      },
    },
    priceAdjustments: [
      {
        price: money(perDeliveryPrice),
        compareAtPrice: money(unitPrice),
        perDeliveryPrice: money(perDeliveryPrice),
      },
    ],
  };
};

const serializeCartLine = (pricedLine: PricedCartLine) => {
  const { line, perDeliveryPrice, product, subtotal, total, unitPrice } = pricedLine;

  return {
    __typename: "CartLine" as const,
    id: line.id,
    quantity: line.quantity,
    sellingPlanAllocation: serializeSellingPlanAllocation(pricedLine),
    cost: {
      totalAmount: money(total),
      subtotalAmount: money(subtotal),
      amountPerQuantity: money(perDeliveryPrice),
    },
    merchandise: {
      __typename: "ProductVariant" as const,
      id: toVariantGid(product.variantId),
      title: DEFAULT_VARIANT_TITLE,
      sku: product.sku,
      image: { url: product.images[0] },
      price: money(unitPrice),
      product: {
        id: `gid://shopify/Product/${product.shopifyProductId}`,
        title: product.name,
        handle: product.handle,
        metafields: [
          { namespace: "custom", key: SHORT_NAME_METAFIELD_KEY, value: product.shortName },
        ],
      },
    },
  };
};

const serializeDeliveryOption = (option: ShippingOption) => {
  return {
    handle: option.handle,
    code: option.code,
    title: option.title,
    description: option.description,
    deliveryMethodType: DELIVERY_METHOD,
    estimatedCost: money(option.amount),
  };
};

export const serializeMockCart = (cart: MockCart, shopifyOrigin: string) => {
  const discounts = cart.discountCodes.flatMap((code) => {
    const discount = findDiscountCode(code);

    return discount ? [discount] : [];
  });
  const lineDiscounts = discounts.filter((discount) => {
    return discount.scope === "line";
  });
  const pricedLines = cart.lines.flatMap((line) => {
    return priceCartLine(line, lineDiscounts);
  });
  const linesTotal = sum(
    pricedLines.map(({ total }) => {
      return total;
    }),
  );

  let remainingAmount = linesTotal;
  const orderAllocations = discounts
    .filter((discount) => {
      return discount.scope === "order";
    })
    .map((discount) => {
      const amount = getDiscountAmount(discount, remainingAmount);
      remainingAmount = roundToCents(remainingAmount - amount);

      return {
        __typename: "CartCodeDiscountAllocation" as const,
        code: discount.code,
        discountedAmount: money(amount),
      };
    });

  const shippingOption = pricedLines.length > 0 ? MOCK_SHIPPING_OPTIONS.STANDARD : null;

  return {
    id: cart.id,
    checkoutUrl: `${shopifyOrigin}/checkouts/cn/${cart.token}`,
    attributes: cart.attributes,
    cost: {
      checkoutChargeAmount: money(
        sum(
          pricedLines.map(({ subtotal }) => {
            return subtotal;
          }),
        ),
      ),
      subtotalAmount: money(linesTotal),
      totalAmount: money(roundToCents(remainingAmount + (shippingOption?.amount ?? 0))),
    },
    discountAllocations: orderAllocations,
    deliveryGroups: {
      nodes: shippingOption
        ? [
            {
              deliveryOptions: Object.values(MOCK_SHIPPING_OPTIONS).map(serializeDeliveryOption),
              selectedDeliveryOption: serializeDeliveryOption(shippingOption),
            },
          ]
        : [],
    },
    lines: {
      edges: pricedLines.map((pricedLine) => {
        return { node: serializeCartLine(pricedLine) };
      }),
    },
  };
};

const serializeSellingPlan = (sellingPlan: MockSellingPlan) => {
  const policy = { interval: SELLING_PLAN_INTERVAL, intervalCount: sellingPlan.intervalCount };

  return {
    id: sellingPlan.id,
    name: sellingPlan.name,
    recurringDeliveries: true,
    options: [{ name: "Delivery every", value: sellingPlan.optionValue }],
    deliveryPolicy: { __typename: "SellingPlanRecurringDeliveryPolicy" as const, ...policy },
    billingPolicy: { __typename: "SellingPlanRecurringBillingPolicy" as const, ...policy },
  };
};

export const serializeSellingPlanNode = (variantGid: string) => {
  const variantId = fromVariantGid(variantGid);
  const product = variantId ? findCatalogProductByVariantId(variantId) : undefined;

  if (!product) {
    return null;
  }

  const unitPrice = toDollars(product.priceInCents);

  return {
    __typename: "ProductVariant" as const,
    id: variantGid,
    title: DEFAULT_VARIANT_TITLE,
    sellingPlanAllocations: {
      edges: MOCK_SELLING_PLANS.map((sellingPlan) => {
        const perDeliveryPrice = roundToCents(
          unitPrice * (1 - sellingPlan.discountPercentage / 100),
        );

        return {
          node: {
            sellingPlan: serializeSellingPlan(sellingPlan),
            priceAdjustments: [
              {
                price: money(perDeliveryPrice),
                compareAtPrice: money(unitPrice),
                perDeliveryPrice: money(perDeliveryPrice),
              },
            ],
          },
        };
      }),
    },
  };
};
